/**
 * Super-Admin "View As" — a perspective, not a login.
 *
 * `ViewAsLauncher` mints a short-lived read-only token for a lower tier and
 * reloads the app; `ViewAsBanner` sits above everything while the preview is
 * live so nobody forgets, and exits in one click.
 *
 * The preview token lives in sessionStorage (`admin_view_as`) and takes
 * precedence in `lib/authToken.getToken()`, so every API call speaks as the
 * previewed tier until you exit.
 */
import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Eye, ShieldOff, X } from 'lucide-react';
import { toast } from 'sonner';

const KEY = 'admin_view_as';

export const readViewAs = () => {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_e) { return null; }
};

const start = (payload) => {
  try { sessionStorage.setItem(KEY, JSON.stringify(payload)); } catch (_e) { /* noop */ }
  window.location.reload();
};

export const exitViewAs = () => {
  try { sessionStorage.removeItem(KEY); } catch (_e) { /* noop */ }
  window.location.reload();
};

export const ViewAsBanner = () => {
  const active = readViewAs();
  if (!active) return null;
  return (
    <div className="sticky top-0 z-[60] flex items-center gap-3 px-4 py-2 bg-amber-500 text-amber-950 text-sm shadow"
         data-testid="view-as-banner">
      <Eye className="h-4 w-4 shrink-0" />
      <span>
        Viewing as <strong>{active.label}</strong> — read-only preview.
        {active.actor_email ? ` You are still signed in as ${active.actor_email}.` : ''}
      </span>
      <Button size="sm" variant="secondary" className="ml-auto h-7"
              data-testid="view-as-exit" onClick={exitViewAs}>
        <X className="h-3.5 w-3.5 mr-1" /> Exit preview
      </Button>
    </div>
  );
};

export const ViewAsLauncher = () => {
  const active = readViewAs();

  const { data } = useQuery({
    queryKey: ['view-as-roles'],
    queryFn: async () => (await api.get('/admin/view-as/roles')).data,
    enabled: !active,
    retry: false,
  });

  const startMutation = useMutation({
    mutationFn: async (role) => (await api.post('/admin/view-as', { role })).data,
    onSuccess: (res) => {
      toast.success(`Switching to the ${res.label} view…`);
      start({ token: res.token, role: res.role, label: res.label,
              actor_email: '', expires_at: res.expires_at });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Card data-testid="view-as-launcher">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Eye className="h-5 w-5 text-amber-500" /> View As — role perspective
        </CardTitle>
        <CardDescription>
          See the platform exactly as a lower tier sees it, then switch back in one click.
          Previews are <strong>read-only</strong> and expire on their own.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {active ? (
          <div className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
               data-testid="view-as-active">
            <Badge className="bg-amber-500 hover:bg-amber-500">{active.label}</Badge>
            <span className="text-sm text-amber-900">Preview is live.</span>
            <Button size="sm" variant="outline" className="ml-auto"
                    data-testid="view-as-exit-card" onClick={exitViewAs}>
              Exit preview
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {(data?.roles || []).map((r) => (              <button
                key={r.key}
                onClick={() => startMutation.mutate(r.key)}
                disabled={startMutation.isPending}
                data-testid={`view-as-${r.key}`}
                className="text-left rounded-lg border p-3 hover:border-amber-400 hover:shadow-sm transition-all disabled:opacity-60"
              >
                <p className="font-semibold text-slate-900 text-sm">{r.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{r.blurb}</p>
                <p className="text-[11px] text-slate-400 mt-1">{r.capabilities} capabilities</p>
              </button>
            ))}
          </div>
        )}
        <p className="text-xs text-slate-500 flex items-center gap-1">
          <ShieldOff className="h-3 w-3" />
          Every write is refused while previewing, and each preview is written to the
          super-admin audit log.
        </p>
      </CardContent>
    </Card>
  );
};

export default ViewAsLauncher;
